import { useUnistyles } from 'react-native-unistyles'

export type BreakpointName = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

/**
 * Custom hook to provide common breakpoint checks.
 * Uses useUnistyles for reactive breakpoint updates on window resize.
 */
export const useBreakpoint = () => {
  const { rt, containerBreakpoint } = useUnistyles()
  const currentBreakpoint = (containerBreakpoint ??
    rt.breakpoint ??
    'xs') as BreakpointName

  return {
    // Current breakpoint
    current: currentBreakpoint,

    // Common screen size checks
    isXs: currentBreakpoint === 'xs',
    isSm: currentBreakpoint === 'sm',
    isMd: currentBreakpoint === 'md',
    isLg: currentBreakpoint === 'lg',
    isXl: currentBreakpoint === 'xl',
    isXxl: currentBreakpoint === 'xxl',

    // Grouped size checks (commonly used patterns)
    isSmallScreen: ['xs', 'sm'].includes(currentBreakpoint),
    isWideScreen: ['lg', 'xl', 'xxl'].includes(currentBreakpoint),
    isMediumAndUp: ['md', 'lg', 'xl'].includes(currentBreakpoint),
    isLargeAndUp: ['lg', 'xl', 'xxl'].includes(currentBreakpoint),

    // Size comparisons
    isAtLeastSm: !['xs'].includes(currentBreakpoint),
    isAtLeastMd: !['xs', 'sm'].includes(currentBreakpoint),
    isAtLeastLg: !['xs', 'sm', 'md'].includes(currentBreakpoint),
    isAtLeastXl: ['xl', 'xxl'].includes(currentBreakpoint),

    // Utility functions for custom checks
    isOneOf: (breakpoints: BreakpointName[]) =>
      breakpoints.includes(currentBreakpoint),
    isNot: (breakpoint: BreakpointName) => currentBreakpoint !== breakpoint,
  }
}
